import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Hidden from '@material-ui/core/Hidden';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import moment from 'moment';
import cx from 'classnames';
import Router from 'next/router';
import { parse } from 'url';
import { flatMap, assign } from 'lodash';
import SortIcon from '../../shared/icons/Sort';
import Section from '../UiSection';
import TitleSearch from './TitleSearch';
import TagFilter from './TagFilter';
import GroupFilters from './GroupFilters';
import Results from './Results';
import TimeFilter from './TimeFilter';
import GridHidden from '../GridHidden';
import { AttributeFilter } from './AttributeFilter';

moment.locale('de');

const useStyles = makeStyles(theme => ({
  uiSection: {
    marginTop: theme.spacing(8),
  },
  noMargin: {
    margin: 0,
    padding: 0,
    paddingBottom: theme.spacing(5),
  },
  wrap: {
    ...theme.mixins.maxWidth(),
    padding: theme.spacing(1),
  },
  headline: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  filterHeadline: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  sortContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  resultCount: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
}));

const filterByQuery = (query, queryAttributes) => p => {
  if (!query || query === '' || query.length < 3) return true;
  return queryAttributes.some(attr => {
    if (typeof attr === 'function') {
      return attr(p, query.toLowerCase());
    } else {
      return p[attr] && p[attr].toLowerCase().includes(query.toLowerCase());
    }
  });
};

const filterByTimeRange = (hasTimeFilter, from, to) => p => {
  if (!hasTimeFilter) return true;
  if (!from) return true;
  const pageDate = moment(p.endDate || p.startDate || p.updatedAt);
  if (!to) return pageDate.isAfter(from);
  return pageDate.isBetween(from, moment(to).endOf('day'));
};

const filterByTags = tags => p => {
  if (tags.length === 0) return true;
  return tags.every(t => p.tags.includes(t));
};

const filterByGroups = (groups, accessor) => p => {
  if (groups.length === 0) return true;
  const group = accessor(p);
  return groups.includes(group);
};

const enhanceDataForGrouping = (data, { duplicateByKey, isGroupValid }) => {
  if (!duplicateByKey) return data;
  return flatMap(data, c => {
    const rest = c[duplicateByKey].filter(cc => !isGroupValid(cc));
    return (
      c[duplicateByKey] &&
      c[duplicateByKey]
        .filter(isGroupValid)
        .map(cc => assign({}, c, { [duplicateByKey]: [cc, ...rest] }))
    );
  });
};

const sortingAttributeExists = sorting => pages =>
  sorting.accessor
    ? pages.some(sorting.accessor)
    : pages.some(p => p[sorting.attribute]);

const sortData = sorting => (a, b) => {
  const valueA = sorting.accessor
    ? sorting.accessor(a).toLowerCase()
    : a[sorting.attribute].toLowerCase();
  const valueB = sorting.accessor
    ? sorting.accessor(b).toLowerCase()
    : b[sorting.attribute].toLowerCase();
  // if (a.updatedAt < b.updatedAt) return 1;
  // if (a.updatedAt > b.updatedAt) return -1;
  // TODO: Make sure we dont need createdAt sorting...
  // if (a.updatedAt || a.createdAt < b.updatedAt || b.createdAt) return 1;
  // if (a.updatedAt || a.createdAt > b.updatedAt || b.createdAt) return -1;
  // if (a.startDate < b.startDate) return 1;
  // if (a.startDate > b.startDate) return -1;
  const dir = sorting.direction === 'up' ? 1 : -1;
  return valueA.localeCompare(valueB) * dir;
};

const getInitialFilterAttributes = (initial, definitions) => {
  if (initial && Object.keys(initial).length > 0) return initial;
  return (
    definitions?.reduce((acc, fd) => {
      if (fd.default) {
        acc[fd.id] = fd.default;
      }
      return acc;
    }, {}) || {}
  );
};

const Filter = props => {
  const {
    titleProps,
    sortingDefault,
    duplicateByKey,
    isGroupValid,
    queryAttributes,
    noTitle,
    filterLabel,
    hasTimeFilter,
    noMargin,
    showLoading,
    groupFiltersLabel,
    additionalInfo,
  } = props;
  const [prevQuery, setPrevQuery] = useState(null);
  const [query, setQuery] = useState(props.query);
  const [sorting, setSorting] = useState(sortingDefault);
  const [filters, setFilters] = useState(props.filters);
  const [timeFilterFrom, setTimeFilterFrom] = useState(
    props.timeFilterFrom || moment().startOf('day'),
  );
  const [timeFilterTo, setTimeFilterTo] = useState(props.timeFilterTo);
  const [groupFilters, setGroupFilters] = useState(props.groupFilters);
  const [attributeFilters, setAttributeFilters] = useState(
    getInitialFilterAttributes(
      props.attributeFilters,
      props.attributeFilterDefinitions,
    ),
  );
  const classes = useStyles();
  const theme = useTheme();
  const hasGroupFilters = groupFilters.length > 0;
  if (props.query !== prevQuery) {
    setQuery(props.query);
    setPrevQuery(props.query);
  }

  const handleResetTimeFilter = () => {
    setTimeFilterFrom(null);
    setTimeFilterTo(null);
  };

  const handleTimeFilterFromChange = d => {
    const date = d.startOf('day');
    setTimeFilterFrom(date);
  };

  const handleTimeFilterToChange = d => {
    const date = d.endOf('day');
    setTimeFilterTo(date);
  };

  const handleTitleSearch = q => {
    setQuery(q);
  };

  const handleResetFilter = filterAttribute => {
    const filteredFilters = filters.filter(f => !f.includes(filterAttribute));
    setFilters(filteredFilters);
  };

  const handleClickFilter = tag => {
    if (filters.includes(tag)) {
      const copy = [...filters];
      const index = filters.indexOf(tag);
      copy.splice(index, 1);
      setFilters(copy);
    } else {
      setFilters([...filters, tag]);
    }
  };

  const handleResetGroupFilter = () => {
    setGroupFilters([]);
  };

  const handleChangeAttributeFilter = (key, value) => {
    setAttributeFilters(f => ({ ...f, [key]: value }));
  };

  const handleClickGroupFilter = group => {
    if (groupFilters.includes(group)) {
      const copy = [...groupFilters];
      const index = groupFilters.indexOf(group);
      copy.splice(index, 1);
      setGroupFilters(copy);
    } else {
      setGroupFilters([...groupFilters, group]);
    }
  };

  const handleClickSorting = attribute => {
    if (sorting.attribute === attribute) {
      setSorting({
        attribute,
        direction: sorting.direction === 'down' ? 'up' : 'down',
      });
    } else {
      setSorting({
        attribute,
        direction: 'down',
      });
    }
  };

  useEffect(() => {
    const routerQuery = {
      ...Router.query,
      filters,
      groupFilters,
      q: query,
      ...attributeFilters,
    };
    const parsed = parse(Router.asPath);
    Router.replace(
      {
        pathname: Router.pathname,
        query: routerQuery,
        hash: window.location.hash,
      },
      {
        pathname: parsed.pathname || Router.route,
        query: routerQuery,
        hash: window.location.hash,
      },
      { shallow: true },
    );
  }, [filters, groupFilters, query, attributeFilters]);

  const accessor = props.sortings.find(s => sorting.attribute === s.attribute)
    .accessor;

  const enhancedData = enhanceDataForGrouping(props.data, {
    duplicateByKey,
    isGroupValid,
  });

  const activeFilterAttributes = useMemo(() => {
    return Object.keys(attributeFilters).reduce((acc, key) => {
      const filterValue = attributeFilters[key];
      if (
        filterValue !== '' &&
        !!filterValue &&
        props.attributeFilterDefinitions.find(fd => fd.id === key)
      ) {
        acc[key] = filterValue;
      }
      return acc;
    }, {});
  }, [attributeFilters, props.attributeFilterDefinitions]);

  const hasAttributeFilters = useMemo(
    () =>
      activeFilterAttributes && Object.keys(activeFilterAttributes)?.length > 0,
    [attributeFilters],
  );

  const filteredData = enhancedData
    .filter(filterByQuery(query, queryAttributes))
    .filter(filterByTags(filters))
    .filter(filterByGroups(groupFilters, props.groupBy))
    .filter(filterByTimeRange(hasTimeFilter, timeFilterFrom, timeFilterTo))
    .filter(c => {
      if (!hasAttributeFilters) return true;
      return Object.keys(activeFilterAttributes).every(attribute =>
        props.attributeFilterDefinitions
          .find(fd => fd.id === attribute)
          .match(activeFilterAttributes[attribute], c),
      );
    })
    .sort(sortData({ ...sorting, accessor }));
  return (
    <React.Fragment>
      <Section
        className={cx(classes.uiSection, { [classes.noMargin]: noMargin })}
      >
        <div className={classes.wrap}>
          {!noTitle && (
            <Grid container spacing={2}>
              <Grid item xs={12} className={classes.headline}>
                <Typography variant="h3" color="primary" {...titleProps}>
                  <b>{props.title}</b>
                </Typography>
              </Grid>
            </Grid>
          )}
          {!showLoading && (
            <React.Fragment>
              {props.filterTags.length > 0 && (
                <React.Fragment>
                  <Grid container spacing={2}>
                    <Grid item xs={12} className={classes.filterHeadline}>
                      <Typography variant="h5">{filterLabel}</Typography>
                    </Grid>
                  </Grid>
                  {props.filterTags.map(tag => (
                    <TagFilter
                      key={tag}
                      filterAttribute={tag}
                      data={props.data}
                      filteredData={filteredData}
                      onResetFilter={handleResetFilter}
                      onClickFilter={handleClickFilter}
                      activeFilters={filters}
                    />
                  ))}
                </React.Fragment>
              )}
              {hasTimeFilter && (
                <TimeFilter
                  timeFilterTo={timeFilterTo}
                  timeFilterFrom={timeFilterFrom}
                  onChangeFrom={handleTimeFilterFromChange}
                  onChangeTo={handleTimeFilterToChange}
                  onReset={handleResetTimeFilter}
                />
              )}
              {props.filterByGroups && props.groupBy && (
                <React.Fragment>
                  {groupFiltersLabel && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} className={classes.filterHeadline}>
                        <Typography variant="h5">
                          {groupFiltersLabel}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  <GroupFilters
                    data={enhancedData}
                    filteredData={filteredData}
                    labels={props.groupLabels}
                    groupBy={props.groupBy}
                    activeFilters={groupFilters}
                    onClickFilter={handleClickGroupFilter}
                    onResetFilter={handleResetGroupFilter}
                  />
                </React.Fragment>
              )}
              {props.attributeFilterDefinitions?.length > 0 && (
                <Grid container spacing={2} style={{ marginBottom: 8 }}>
                  <GridHidden item xsDown sm={2} />
                  <Grid item xs={10} sm={8}>
                    <Box display="flex" style={{ gap: 16, flexWrap: 'wrap' }}>
                      {props.attributeFilterDefinitions.map(fd => (
                        <AttributeFilter
                          key={fd.id}
                          id={fd.id}
                          label={fd.label}
                          noEmpty={fd.noEmpty}
                          emptyLabel={fd.emptyLabel}
                          value={attributeFilters?.[fd.id] || ''}
                          options={fd.options}
                          onChange={handleChangeAttributeFilter}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              )}
              {!props.noSearch && (
                <TitleSearch
                  placeholder={props.searchPlaceholder}
                  value={query}
                  onChange={handleTitleSearch}
                />
              )}
              <Grid container spacing={2}>
                <GridHidden item sm={2} only="xs" />
                <Grid item xs={12} sm={8} className={classes.sortContainer}>
                  {!props.noSorting &&
                    props.sortings.map(s => (
                      <Button
                        key={s.attribute}
                        variant="text"
                        color="default"
                        style={{
                          opacity: s.attribute === sorting.attribute ? 1 : 0.5,
                        }}
                        disabled={!sortingAttributeExists(s)(filteredData)}
                        endIcon={
                          <SortIcon
                            color="disabled"
                            upActive={
                              s.attribute === sorting.attribute &&
                              sorting.direction === 'up'
                            }
                            downActive={
                              s.attribute === sorting.attribute &&
                              sorting.direction === 'down'
                            }
                            activeColor={theme.palette.primary.main}
                          />
                        }
                        onClick={() => handleClickSorting(s.attribute)}
                      >
                        {s.label}
                      </Button>
                    ))}
                </Grid>
                <GridHidden item sm={2} only="xs" />
              </Grid>
            </React.Fragment>
          )}
        </div>
      </Section>
      {!showLoading && (
        <React.Fragment>
          {additionalInfo}
          <Typography variant="body1" className={classes.resultCount}>
            Gefundene Ergebnisse ({filteredData.length})
          </Typography>
          <Results
            data={filteredData}
            groupBy={props.groupBy}
            context={props.context}
            groupLabels={props.groupLabels}
          />
        </React.Fragment>
      )}
      {showLoading && (
        <Box
          height={400}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <Box mb={2}>
            <CircularProgress />
          </Box>
          <Typography variant="body1">Inhalte werden geladen</Typography>
        </Box>
      )}
    </React.Fragment>
  );
};

Filter.propTypes = {
  showLoading: PropTypes.bool,
  noMargin: PropTypes.bool,
  hasTimeFilter: PropTypes.bool,
  context: PropTypes.string,
  title: PropTypes.string.isRequired,
  filterLabel: PropTypes.string,
  titleProps: PropTypes.object,
  searchPlaceholder: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  filterTags: PropTypes.array,
  filters: PropTypes.array,
  noTitle: PropTypes.bool,
  noSearch: PropTypes.bool,
  noSorting: PropTypes.bool,
  query: PropTypes.string,
  queryAttributes: PropTypes.array,
  groupBy: PropTypes.func,
  sortings: PropTypes.array,
  groupLabels: PropTypes.object,
  filterByGroups: PropTypes.bool,
  groupFilters: PropTypes.array,
  sortingDefault: PropTypes.object,
  isGroupValid: PropTypes.func,
  additionalInfo: PropTypes.element,
};

Filter.defaultProps = {
  additionalInfo: null,
  showLoading: false,
  noMargin: false,
  hasTimeFilter: false,
  context: null,
  title: 'Zertifizierte Unternehmen finden',
  titleProps: {},
  filterLabel: 'Inhalte filtern nach',
  subtitle: null,
  subtitleProps: {},
  searchPlaceholder: 'Unternehmen, Produkte oder ID suchen',
  data: [],
  filterTags: [],
  filters: [],
  timeFilterFrom: null,
  timeFilterTo: null,
  filterByGroups: false,
  groupFilters: [],
  noTitle: false,
  noSearch: false,
  noSorting: false,
  query: '',
  queryAttributes: ['title'],
  groupBy: null,
  groupLabels: null,
  sortings: [
    {
      attribute: 'title',
      label: 'Alphabetisch sortieren',
    },
  ],
  sortingDefault: {
    attribute: 'title',
    direction: 'up',
  },
  isGroupValid: () => true,
  groupFiltersLabel: null,
};

export default Filter;
